import React, { useState } from 'react';
import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CustomButton from 'components/CustomInput/Button/index';
import { useSelector } from 'react-redux';
import MsgBox from 'components/MsgBox/MsgBox';
import { generateRandomId } from 'utility/general';
import styled from 'styled-components';
import AddIcon from '@material-ui/icons/Add';
import { getSystemMessage } from 'store/system/systemMessageReducer';

const useStyles = makeStyles((theme) => ({
    labelCls: {
        marginRight: 8,
    },
}));

const SInputStyle = styled.div`
    border: 1px solid #b7b7b7;
    border-radius: 3px;
    display: flex;
    background: ${(props) => (props.disabled ? '#f7f7f7' : '#fff')};
    justify-content: space-between;
    align-items: center;
    height: 37px;
    width: 100%;
`;

export default React.forwardRef((props, ref) => {
    const classes = useStyles();
    const [id] = useState(generateRandomId());

    const {
        labelCls,
        label,
        accept,
        fileOnChange,
        multiple,
        params,
        buttonText = 'Upload',
        maxFileSize,
        disabled,
        loading,
        disableOnPermission = false,
        inputStyle = false,
        inputStyleText,
        optional = false,
        isLoading,
        ...rest
    } = props;

    const systemMessage = useSelector((state) => state.systemMessage.systemMessage);
    const allConfig = useSelector(state => state.system.allConfig);
    const uploadFileMaxSize = maxFileSize ?? allConfig.uploadFileMaxSize;
    const executableFiles = allConfig.executableFiles ?? ['.bat', '.bin', '.cmd', '.com', '.exe'];
    const sizeDisplay = uploadFileMaxSize/1024/1024;

    const [warningMsg, setWarningMsg] = useState(null);

    const isValidFiles = (files) => {
        let valid = true,
            errorMsg = [];
        for (let i of files) {
            const fileName = i.name;
            const suffix = fileName.slice(fileName.lastIndexOf('.'));
            /** can't upload: '.bat', '.bin', '.cmd', '.com', '.exe'  */
            if (executableFiles.includes(suffix)) {
                errorMsg.push(systemMessage['uploadError_002']);
                valid = false;
            }

            /** each file size limited 50MB */
            if (uploadFileMaxSize && i.size > uploadFileMaxSize) {
                errorMsg.push(getSystemMessage('uploadError_001', [`${sizeDisplay}MB`]));
                valid = false;
            }

            /** can't upload file's size = 0MB */
            if (i.size === 0) {
                errorMsg.push(systemMessage['uploadError_003']);
                valid = false;
            }

            if (!valid) break;
        }
        if (errorMsg.length) {
            setWarningMsg({
                message: errorMsg,
                footerActions: [
                    {
                        label: 'Confirm',
                        clicked: () => {
                            setWarningMsg(null);
                        },
                    },
                ],
            });
        }

        return valid;
    };

    const handleFileOnChange = (el) => {
        const files = el.target.files;
        if (!isValidFiles(files)) return;

        let list = [],
            isLimitSize = true;
        for (let i of files) {
            list.push(i);
        }

        if (files.length && isLimitSize) {
            fileOnChange(list);
        }
    };

    const isDisabled = loading || disableOnPermission || (disabled ?? false);

    const getUploadButton = () => {
        return (
            <Grid item>
                <input
                    style={{ display: 'none', width: '100%' }}
                    id={`${id}_input`}
                    name={`${id}_input`}
                    type="file"
                    accept={accept ? accept : ''} //".key, .txt, .ppt..."
                    onChange={(e) => handleFileOnChange(e)}
                    ref={ref}
                    multiple={multiple}
                    disabled={loading || disableOnPermission || (disabled ?? false)}
                    {...rest}
                    value=""
                />
                <label
                    htmlFor={`${id}_input`}
                    style={{
                        paddingRight: 8,
                        width: 0,
                    }}
                >
                    {inputStyle ? (
                        <div style={{ marginTop: 19, position: 'relative' }}>
                            <div style={{ paddingRight: 5, position: 'absolute', top: -13, right: 0 }}>
                                <AddIcon style={{ width: 22, height: 22, color: isDisabled ? '#cccccc' : '#000' }} />
                            </div>
                        </div>
                    ) : (
                        <CustomButton
                            id={`${id}_upload_btn`}
                            variant="contained"
                            component="span"
                            disabled={loading || disableOnPermission || (disabled ?? false)}
                            loading={isLoading}
                        >
                            {loading ? 'Uploading' : buttonText}
                        </CustomButton>
                    )}
                </label>
            </Grid>
        );
    };

    return (
        <>
            {warningMsg && (
                <MsgBox
                    type={warningMsg.type}
                    message={warningMsg.message}
                    footerActions={warningMsg.footerActions}
                    title={warningMsg.title}
                    closePopup={() => setWarningMsg(null)}
                />
            )}
            <Grid container alignItems="center">
                {label && (
                    <Grid item className={labelCls || classes.labelCls}>
                        {label}
                    </Grid>
                )}
                {inputStyle ? (
                    <SInputStyle disabled={isDisabled}>
                        <div
                            style={{
                                height: 37,
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 8,
                                color: isDisabled ? '#cccccc' : '#000',
                            }}
                        >
                            {inputStyleText ?? 'Upload Documents'} {optional ? '(Optional)' : ''}
                        </div>
                        {getUploadButton()}
                    </SInputStyle>
                ) : (
                    getUploadButton()
                )}
            </Grid>
        </>
    );
});
